/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	HttpErrorResponse
} from '@angular/common/http';
import {
	Injectable
} from '@angular/core';
import {
	ApiError
} from '@api/errors/api.error';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';

/**
 * A singleton service for tracking and deduplicating error instances
 * to prevent recursive error handling loops.
 *
 * @export
 * @class ErrorDeduplicationService
 */
@Injectable({
	providedIn: 'root'
})
export class ErrorDeduplicationService
{
	/**
	 * Creates an instance of ErrorDeduplicationService.
	 *
	 * @memberof ErrorDeduplicationService
	 */
	public constructor()
	{
	}

	/**
	 * Gets or sets the time window duration in milliseconds for considering
	 * errors as duplicates based on their signature.
	 *
	 * @type {number}
	 * @memberof ErrorDeduplicationService
	 */
	public windowDurationMs: number = 1000;

	/**
	 * Gets or sets the WeakMap tracking error instances to detect
	 * true recursive errors (same object reference).
	 *
	 * @type {WeakMap<object, boolean>}
	 * @memberof ErrorDeduplicationService
	 */
	private readonly errorInstances: WeakMap<object, boolean> =
		new WeakMap<object, boolean>();

	/**
	 * Gets or sets the Map tracking error signatures with timestamps
	 * to detect structurally identical errors within the time window.
	 *
	 * @type {Map<string, number>}
	 * @memberof ErrorDeduplicationService
	 */
	private readonly errorSignatures: Map<string, number> =
		new Map<string, number>();

	/**
	 * Determines if the provided error is a duplicate that should be
	 * skipped to prevent recursive error handling loops.
	 *
	 * @param {ApiError | Error | HttpErrorResponse} error
	 * The error to check for duplication.
	 * @returns {boolean}
	 * True if the error is a duplicate; otherwise, false.
	 * @memberof ErrorDeduplicationService
	 */
	public isDuplicate(
		error: ApiError | Error | HttpErrorResponse): boolean
	{
		// Check if this exact error instance was already handled.
		if (this.errorInstances.has(error))
		{
			return true;
		}

		// Mark this instance as handled.
		this.errorInstances.set(
			error,
			true);

		// Generate signature and check for structural duplicates.
		const signature: string = this.getErrorSignature(error);
		const currentTimestamp: number = Date.now();

		// Clean expired signatures before checking.
		this.cleanExpiredSignatures(currentTimestamp);

		// Check if this signature was recently handled.
		if (this.errorSignatures.has(signature))
		{
			return true;
		}

		// Record this signature with current timestamp.
		this.errorSignatures.set(
			signature,
			currentTimestamp);

		return false;
	}

	/**
	 * Generates a unique signature string for the error based on its
	 * type, message, and stack trace to identify structurally identical errors.
	 *
	 * @private
	 * @param {ApiError | Error | HttpErrorResponse} error
	 * The error to generate a signature for.
	 * @returns {string}
	 * A signature string uniquely identifying this error type and content.
	 * @memberof ErrorDeduplicationService
	 */
	private getErrorSignature(
		error: ApiError | Error | HttpErrorResponse): string
	{
		const errorType: string =
			AnyHelper.isNull(error?.constructor?.name)
				? 'UnknownError'
				: error.constructor.name;
		const errorMessage: string =
			AnyHelper.isNull(error?.message)
				? AppConstants.empty
				: error.message;

		// Handle HttpErrorResponse with status code.
		if (error instanceof HttpErrorResponse)
		{
			const statusCode: number =
				AnyHelper.isNull(error.status)
					? 0
					: error.status;
			const url: string =
				AnyHelper.isNull(error.url)
					? AppConstants.empty
					: error.url;

			return `${errorType}:${statusCode}:${url}:${errorMessage}`;
		}

		// Handle ApiError with title
		if (error instanceof ApiError)
		{
			const title: string =
				AnyHelper.isNull(error.title)
					? AppConstants.empty
					: error.title;

			return `${errorType}:${title}:${errorMessage}`;
		}

		// Handle base Error with stack trace
		const stackLines: string[] =
			AnyHelper.isNull(error?.stack)
				? []
				: error.stack.split('\n');
		const firstStackLine: string =
			stackLines.length > 0 ? stackLines[0] : AppConstants.empty;

		return `${errorType}:${errorMessage}:${firstStackLine}`;
	}

	/**
	 * Removes expired error signatures from the tracking map based
	 * on the configured time window duration.
	 *
	 * @private
	 * @param {number} currentTimestamp
	 * The current timestamp to compare against stored timestamps.
	 * @memberof ErrorDeduplicationService
	 */
	private cleanExpiredSignatures(
		currentTimestamp: number): void
	{
		const expirationThreshold: number =
			currentTimestamp - this.windowDurationMs;

		for (const [signature, timestamp] of this.errorSignatures.entries())
		{
			if (timestamp < expirationThreshold)
			{
				this.errorSignatures.delete(signature);
			}
		}
	}
}